import { Link } from 'react-router-dom';
import { CHAPTERS } from '@/data/chapters';
import { APP_ROUTES } from '@/navigation';
import '@/learn.css';

const STUDY_SUMMARIES: Record<string, string> = {
  inversions: 'The core chord types with each chord tone on top.',
  'voice-leading-inversions': 'Connecting those shapes with a moving top voice.',
  'tensions-on-top': 'Ninths and altered ninths as the top voice.',
  'voice-leading-chord-scales': 'A fixed top line through major and minor ii–V–I progressions.',
  'subs-symmetry': 'Tritone substitutes, diminished chords and shared shapes.',
  'song-examples': 'Standards and reharmonisation studies.',
};

interface LearnResource {
  title: string;
  description: string;
  to: string;
}

const START: LearnResource[] = [
  {
    title: 'Where to start',
    description:
      'For guitarists: drop 2, the most common chord types and progressions, and example tunes.',
    to: APP_ROUTES.learnStart,
  },
  {
    title: 'How the app works',
    description:
      'The workbench, backing band, bass, voice-leading options, chord melody, reharmonisation and readings, with example tunes.',
    to: APP_ROUTES.guide,
  },
];

const FOUNDATIONS: LearnResource[] = [
  {
    title: 'How four-note voicings work',
    description: 'Chord quality, voicing family, top voice, finished inversion and fingering.',
    to: APP_ROUTES.fourNoteVoicings,
  },
  {
    title: 'Drop 2',
    description: 'How drop 2 voicings are built, their inversions and how to connect them.',
    to: APP_ROUTES.drop2Intro,
  },
  {
    title: 'Drop 3',
    description: 'How drop 3 voicings are built, their two string sets and their inversions.',
    to: APP_ROUTES.drop3Intro,
  },
];

const PRACTICE: LearnResource[] = [
  {
    title: 'Practise a tune',
    description: 'Open a tune as a chart, with backing or as a chord-melody arrangement.',
    to: APP_ROUTES.workbench,
  },
];

const REFERENCE: LearnResource[] = [
  {
    title: 'Chord library',
    description: 'Every chord type, voicing family and inversion.',
    to: APP_ROUTES.chordLibrary,
  },
  {
    title: 'Scale harmony',
    description: 'One inversion pattern through the chords of a major or minor scale.',
    to: APP_ROUTES.scaleHarmony,
  },
  {
    title: 'Top-note finder',
    description: 'A voicing with a chosen melody note on top.',
    to: APP_ROUTES.topNoteFinder,
  },
];

const EXPLORE: LearnResource[] = [
  {
    title: 'Lab',
    description: 'Interactive diagrams of chord relationships.',
    to: APP_ROUTES.lab,
  },
  {
    title: 'Corpus',
    description: 'Statistics on the chords, keys and progressions in the library.',
    to: APP_ROUTES.corpus,
  },
];

function ResourceList({ resources }: { resources: LearnResource[] }) {
  return (
    <div className="learn-index-list">
      {resources.map((resource) => (
        <Link key={resource.to} to={resource.to}>
          <strong>{resource.title}</strong>
          <span>{resource.description}</span>
        </Link>
      ))}
    </div>
  );
}

export function LearnPage() {
  return (
    <div className="learn-index">
      <header className="page-header">
        <h1>Learn</h1>
        <p>Lessons, studies and practice material for four-note guitar voicings.</p>
      </header>

      <section aria-labelledby="learn-start-heading">
        <h2 id="learn-start-heading" className="section-title">
          Start here
        </h2>
        <ResourceList resources={START} />
      </section>

      <section aria-labelledby="learn-foundations-heading">
        <h2 id="learn-foundations-heading" className="section-title">
          Foundations
        </h2>
        <ResourceList resources={FOUNDATIONS} />
      </section>

      <section aria-labelledby="learn-studies-heading">
        <h2 id="learn-studies-heading" className="section-title">
          Drop 2 studies
        </h2>
        <div className="learn-index-list">
          {CHAPTERS.map((chapter) => (
            <Link key={chapter.id} to={`${APP_ROUTES.studies}/${chapter.id}`}>
              <strong>
                {chapter.number}. {chapter.title}
              </strong>
              <span>{STUDY_SUMMARIES[chapter.id] ?? chapter.concept}</span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="learn-practice-heading">
        <h2 id="learn-practice-heading" className="section-title">
          Practice
        </h2>
        <ResourceList resources={PRACTICE} />
      </section>

      <section aria-labelledby="learn-reference-heading">
        <h2 id="learn-reference-heading" className="section-title">
          Reference
        </h2>
        <ResourceList resources={REFERENCE} />
      </section>

      <section aria-labelledby="learn-explore-heading">
        <h2 id="learn-explore-heading" className="section-title">
          Explore
        </h2>
        <ResourceList resources={EXPLORE} />
      </section>
    </div>
  );
}
